import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { AiKeyMissingNotice } from './AiKeyBanner.jsx';
import AiBusy from './AiBusy.jsx';
import { Modal } from './ui';
import { formatDateTime } from '../util/time.js';

// T030 — Company research one-pager.
//
// Opened from a Kanban side drawer (per-rec — the rec's job.company is
// the lookup key) or from a generic company-name input on the dashboard
// (future). Cached per (user × normalised company name); the first hit
// generates, subsequent opens are free.
//
// The verifyBeforeCommitting list is the load-bearing safety mechanism:
// always shown prominently, always non-empty (the service injects a
// safety net if the LLM forgot).

const CONFIDENCE_TONE = {
  high: 'border-emerald-700 text-emerald-300 bg-emerald-950/30',
  medium: 'border-amber-700 text-amber-300 bg-amber-950/20',
  low: 'border-red-800 text-red-400 bg-red-950/20',
};

const CONFIDENCE_LABEL = {
  high: 'High confidence',
  medium: 'Medium · data may be stale',
  low: 'Low · mostly inference',
};

export default function CompanyResearchModal({ companyName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // T290 — kept so the modal can say why it is empty; the toast is gone by
  // the time the operator looks back at the dialog.
  const [err, setErr] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const { user } = useAuth();
  const hasAnthropicKey = !!user?.connectors?.anthropicKey;

  useEffect(() => {
    // T475 — mounting must stop being the guard on spending money.
    //
    // This effect fires `GET /companies`, which GENERATES research — and bills
    // his Anthropic key — on a cache miss. The only thing stopping that from
    // happening on every page load is that the modal is mounted conditionally
    // (`{companyResearchName && <CompanyResearchModal …>}` on both hosts).
    // T422 wants these modals mounted ONCE in a shell, and a shell is mounted
    // always, so the conditional mount cannot keep being the protection.
    //
    // Now the company name is: no name, no request, whatever else is true.
    // Cheap, explicit, and it makes the component safe to mount idle — which
    // is the precondition for moving it, not the move itself.
    if (!String(companyName ?? '').trim()) {
      setLoading(false);
      setData(null);
      setErr('');
      return;
    }
    // No key → don't fire a doomed request that would 401 + toast on every open;
    // the body shows the inline notice instead.
    if (!hasAnthropicKey) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/companies?name=${encodeURIComponent(companyName)}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.company);
        setNotes(r.company.operatorNotes ?? '');
        if (!r.cached) {
          toast(`Research generated for ${companyName}`, { kind: 'success' });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e.body?.error === 'anthropic_unauthorized'
            ? 'Set your Anthropic API key in Settings → Connectors first.'
            : e.body?.message || e.message;
        setErr(msg);
        toast(msg, { kind: 'error' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyName, hasAnthropicKey]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post('/companies/regenerate', { name: companyName });
      setData(r.company);
      toast('Research regenerated', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    } finally {
      setRegenerating(false);
    }
  };

  const saveNotes = async () => {
    if (!data) return;
    setSavingNotes(true);
    try {
      const r = await api.patch(`/companies/${encodeURIComponent(data.companyKey)}/notes`, {
        operatorNotes: notes,
      });
      setData(r.company);
      toast('Notes saved', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    } finally {
      setSavingNotes(false);
    }
  };

  const research = data?.research;
  const notesDirty = data ? notes !== (data.operatorNotes ?? '') : false;

  // T475 — mounted with nothing to research is not a dialog. Renders nothing
  // AND asks for nothing, so the component is safe to leave mounted idle.
  if (!String(companyName ?? '').trim()) return null;

  return (
    <Modal role="dialog" size="lg" scroll labelledById="company-research-title" onClose={onClose}>
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">Company research</p>
            <h2 id="company-research-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">
              {data?.name ?? companyName}
            </h2>
            {data && (
              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {research?.confidence && (
                  <span className={'rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ' + (CONFIDENCE_TONE[research.confidence] ?? '')}>
                    {CONFIDENCE_LABEL[research.confidence]}
                  </span>
                )}
                <span>
                  generation {data.generationCount}
                  {data.generatedAt && ` · ${formatDateTime(data.generatedAt)}`}
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {!hasAnthropicKey ? (
            <AiKeyMissingNotice feature="Company research" />
          ) : (
            loading && (
              <div className="text-sm">
                <AiBusy feature="companyResearch" />
              </div>
            )
          )}
          {/* T290 — a failed fetch used to leave this modal completely empty.
              The catch toasted and returned, `research` stayed null, `loading`
              went false, and the operator was looking at a dialog with a title
              and nothing under it — with the explanation already gone from the
              corner of the screen by the time he looked back. */}
          {!loading && !research && err && (
            <div className="rounded-lg border border-amber-800/60 bg-amber-950/20 p-3">
              <p className="text-sm text-amber-200">Could not research {companyName}</p>
              <p className="mt-1 text-xs text-amber-100/80">{err}</p>
            </div>
          )}
          {research && (
            <div className="space-y-4">
              <section>
                <Heading>Overview</Heading>
                <p className="mt-1 text-sm text-slate-200">{research.overview}</p>
              </section>

              <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Founded" value={research.founded} />
                <Stat label="Size" value={research.size} />
                <Stat label="Stage" value={research.stage} />
              </section>

              {research.techStack?.length > 0 && (
                <section>
                  <Heading>Tech stack</Heading>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {research.techStack.map((t) => (
                      <span key={t} className="rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 text-[11px] text-slate-300">
                        {t}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {research.cultureNotes && (
                <section>
                  <Heading>Culture notes</Heading>
                  <p className="mt-1 text-sm text-slate-300">{research.cultureNotes}</p>
                </section>
              )}

              {research.recentNews && (
                <section>
                  <Heading>Recent news</Heading>
                  <p className="mt-1 text-sm text-slate-300">{research.recentNews}</p>
                </section>
              )}

              {research.redFlags?.length > 0 && (
                <section>
                  <Heading tone="red">Red flags</Heading>
                  <ul className="mt-1 space-y-1 text-sm text-red-300">
                    {research.redFlags.map((f, i) => (
                      <li key={i}>· {f}</li>
                    ))}
                  </ul>
                </section>
              )}

              {research.questionsToAsk?.length > 0 && (
                <section>
                  <Heading>Questions to ask</Heading>
                  <ul className="mt-1 space-y-1 text-sm text-slate-300">
                    {research.questionsToAsk.map((q, i) => (
                      <li key={i}>· {q}</li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="rounded-lg border border-amber-700 bg-amber-950/15 p-3">
                <Heading tone="amber">⚠ Verify before committing</Heading>
                <ul className="mt-2 space-y-1 text-sm text-amber-100">
                  {research.verifyBeforeCommitting.map((v, i) => (
                    <li key={i}>· {v}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-200/80">
                  The model's data is months-to-years stale. Always confirm anything load-bearing before accepting an offer.
                </p>
              </section>

              <section>
                <Heading>Your notes</Heading>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder='e.g. "Recruiter said the team uses Rust internally — confirm during phone screen."'
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Your own scratchpad — survives regeneration of the AI one-pager.
                </p>
              </section>
            </div>
          )}
        </div>

        {data && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
            {hasAnthropicKey && (
              <button
                type="button"
                onClick={regenerate}
                disabled={regenerating}
                className="rounded-md border border-brand-600 px-3 py-1.5 text-xs text-brand-500 hover:bg-brand-600/10 disabled:opacity-50"
                title="Force a fresh generation (training data won't be newer, but the framing may improve)"
              >
                {regenerating ? <AiBusy feature="companyResearch" compact label="Regenerating" /> : '✨ Regenerate'}
              </button>
            )}
            <button
              type="button"
              onClick={saveNotes}
              disabled={!notesDirty || savingNotes}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
            >
              {savingNotes ? 'saving…' : 'Save notes'}
            </button>
          </footer>
        )}
    </Modal>
  );
}

function Heading({ children, tone = 'default' }) {
  const cls =
    tone === 'red'
      ? 'text-red-300'
      : tone === 'amber'
        ? 'text-amber-300'
        : 'text-brand-500';
  return <p className={'text-[10px] uppercase tracking-wide ' + cls}>{children}</p>;
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-200">{value || <span className="text-slate-600">unknown</span>}</p>
    </div>
  );
}
